import React from 'react';
import { AlertTriangle } from 'lucide-react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { SectionQuarantineBanner } from '../../report/SectionQuarantineBanner';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import {
  EditorialCallout,
  EditorialSubsection,
  type EditorialCalloutTone,
} from '../../report/editorial';

/**
 * Determination tier → callout rule/wash tone. A categorical exclusion
 * reads POSITIVE (a genuinely favourable screening outcome). Every other
 * tier (EA, EIS) is a NEUTRAL procedural classification, NOT a warning —
 * so its left-rule/label is a DECORATIVE accent that tracks the theme's
 * brand colour (roleColor 'accent'), matching the Conclusion-card pattern.
 * (Previously these used `warning`, which surfaced the theme's amber/brown
 * status hue as an off-theme decorative rule.)
 */
function determinationTone(determination: string): EditorialCalloutTone {
  const key = determination.trim().toLowerCase();
  if (key.includes('exclusion')) return 'positive';
  return 'accent';
}

/**
 * RTU-1 — Environmental modern decomposition.
 * NEPA/CEQA screening section.
 */
export const NepaScreeningSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const data = report.nepaScreening;
  const hasData = !!data && !!(data.determination || data.rationale);
  const hasSecondary = !!(data?.ceqaApplicability || (data?.keyTriggers && data.keyTriggers.length > 0));

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <SectionQuarantineBanner
        sectionId="nepaScreening.rationale"
        sectionLabel="NEPA Rationale"
        guardStatus={report.sectionGuardStatus?.['nepaScreening.rationale']}
        quarantinedOriginal={report.quarantinedOriginal?.['nepaScreening.rationale']}
        renderedText={report.nepaScreening?.rationale}
      />
      <div className="space-y-4" data-testid="nepa-screening-layout">
        {data?.determination && (
          <EditorialCallout
            theme={theme}
            tone={determinationTone(data.determination)}
            label="Determination"
          >
            <p className="m-0 font-semibold uppercase tracking-wide" style={{ color: roleColor(theme, 'ink') }}>
              {data.determination}
            </p>
            {data.estimatedTimeline && (
              <p className="m-0 text-xs mt-1" style={{ color: roleColor(theme, 'muted') }}>
                Estimated timeline: {data.estimatedTimeline}
              </p>
            )}
          </EditorialCallout>
        )}
        {(data?.rationale || hasSecondary) && (
          <div
            className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4"
            data-testid="nepa-screening-columns"
          >
            {data?.rationale && (
              <EditorialSubsection theme={theme} label="Rationale">{data.rationale}</EditorialSubsection>
            )}
            {hasSecondary && (
              <div className="space-y-4 min-w-0">
                {data?.ceqaApplicability && (
                  <EditorialSubsection theme={theme} label="State Environmental Review">{data.ceqaApplicability}</EditorialSubsection>
                )}
                {data?.keyTriggers && data.keyTriggers.length > 0 && (
                  <EditorialSubsection theme={theme} label="Key Environmental Triggers">
                    <ul className="list-none space-y-1.5 pl-0 m-0">
                      {data.keyTriggers.map((t, i) => (
                        <li key={`trigger-${i}`} className="flex items-start gap-2">
                          <AlertTriangle
                            className="w-3.5 h-3.5 mt-0.5 shrink-0"
                            style={{ color: roleColor(theme, 'warning') }}
                            aria-hidden
                          />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </EditorialSubsection>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </SectionShell>
  );
};
